import NBType, { InferType } from '@netbase/nb-type'
import Category from '../enums/Category'
import ComparisonMode from '../enums/ComparisonMode'
import CountBy from '../enums/CountBy'
import DayHourTimeZone from '../enums/DayHourTimeZone'
import DocumentFormat from '../enums/DocumentFormat'
import EmojiCategory from '../enums/EmojiCategory'
import ExtraInfo from '../enums/ExtraInfo'
import FacetServicePropertySchema from './FacetServicePropertySchema'
import GroupMetric from '../enums/GroupMetric'
import LocalTimeZone from '../enums/LocalTimeZone'
import Measure from '../enums/Measure'
import MediaDomain from '../enums/MediaDomain'
import MediaType from '../enums/MediaType'
import MetricCategory from '../enums/MetricCategory'
import MetricTimeUnit from '../enums/MetricTimeUnit'
import Order from '../enums/Order'
import PayloadSchema from './PayloadSchema'
import PhotoConceptServicePropertySchema from './PhotoConceptServicePropertySchema'
import Precision from '../enums/Precision'
import SortByOptions from '../enums/SortByOptions'
import SupportedMetricSeries from '../enums/SupportedMetricSeries'

/**
 * @typedef {import('../enums/Category').Category} Category
 * @typedef {import('../enums/ComparisonMode').ComparisonMode} ComparisonMode
 * @typedef {import('../enums/CountBy').CountBy} CountBy
 * @typedef {import('../enums/DayHourTimeZone').DayHourTimeZone} DayHourTimeZone
 * @typedef {import('../enums/DocumentFormat').DocumentFormat} DocumentFormat
 * @typedef {import('../enums/EmojiCategory').EmojiCategory} EmojiCategory
 * @typedef {import('../enums/ExtraInfo').ExtraInfo} ExtraInfo
 * @typedef {import('./FacetServicePropertySchema').FacetServiceProperty} FacetServiceProperty
 * @typedef {import('../enums/GroupMetric').GroupMetric} GroupMetric
 * @typedef {import('../enums/LocalTimeZone').LocalTimeZone} LocalTimeZone
 * @typedef {import('../enums/Measure').Measure} Measure
 * @typedef {import('../enums/MediaDomain').MediaDomain} MediaDomain
 * @typedef {import('../enums/MediaType').MediaType} MediaType
 * @typedef {import('../enums/MetricCategory').MetricCategory} MetricCategory
 * @typedef {import('../enums/MetricTimeUnit').MetricTimeUnit} MetricTimeUnit
 * @typedef {import('../enums/Order').Order} Order
 * @typedef {import('./PayloadSchema').Payload} Payload
 * @typedef {import('./PhotoConceptServicePropertySchema').PhotoConceptServiceProperty} PhotoConceptServiceProperty
 * @typedef {import('../enums/Precision').Precision} Precision
 * @typedef {import('../enums/SortByOptions').SortByOptions} SortByOptions
 * @typedef {import('../enums/SupportedMetricSeries').SupportedMetricSeries} SupportedMetricSeries
 */

/**
 * @typedef {object} ServicePropertySchema
 * @property {boolean} advancedFilterMax
 * @property {Category[]} categories
 * @property {ComparisonMode} colComparisonMode
 * @property {CountBy} countBy
 * @property {boolean} deStopWords
 * @property {boolean} dedup
 * @property {boolean} dma
 * @property {string[]} docNames
 * @property {DocumentFormat} documentFormat
 * @property {EmojiCategory} emojiCategory
 * @property {ExtraInfo} extraInfo
 * @property {FacetServiceProperty} facetServiceProperties
 * @property {string[]} fields
 * @property {boolean} forceCheckout
 * @property {boolean} forceNotScale
 * @property {boolean} groupInfo
 * @property {GroupMetric[]} groupMetrics
 * @property {boolean} hashedAuthorIds
 * @property {boolean} highlightInfo
 * @property {boolean} ignoreSentiment
 * @property {boolean} includeHeaderTweet
 * @property {boolean} keepLongInsights
 * @property {boolean} localized
 * @property {number} maxExactItem
 * @property {boolean} maxPayload
 * @property {Measure} measure
 * @property {MediaDomain[]} mediaDomains
 * @property {MediaType[]} mediaTypes
 * @property {MetricCategory} metricCategory
 * @property {string[]} metricNames
 * @property {SupportedMetricSeries[]} metricSeries
 * @property {number[]} multipleLevelSizeNeeded
 * @property {number} offset
 * @property {Order} order
 * @property {number} pageIndex
 * @property {number} pageSize
 * @property {Payload} payload
 * @property {number} peerCount
 * @property {PhotoConceptServiceProperty} photoConceptServiceProperties
 * @property {DayHourTimeZone} postActivityTimeZone
 * @property {Precision} precision
 * @property {number} priority
 * @property {boolean} removeTweetId
 * @property {boolean} renewImageUrls
 * @property {boolean} returnAuthorFingerprint
 * @property {ComparisonMode} rowComparisonMode
 * @property {string[]} rowDocNames
 * @property {string[]} rowTagNames
 * @property {number[]} rowThemeIds
 * @property {boolean} showPrimaryObject
 * @property {number} sizeNeeded
 * @property {boolean} smoothing
 * @property {string} sort
 * @property {SortByOptions} sortBy
 * @property {number} soundBiteSamples
 * @property {string[]} tagNames
 * @property {number[]} themeIds
 * @property {MetricTimeUnit[]} timeUnits
 * @property {LocalTimeZone} timeZone
 * @property {number} totalCount
 * @property {boolean} turnOffCountSearch
 * @property {boolean} youTubeOnly
 */
const ServicePropertySchema = NBType.object().shape('ServicePropertySchema', {
  /* @prop */
  advancedFilterMax: NBType.boolean(),
  /* @prop */
  categories: NBType.array().of(NBType.enum(Category)),
  /* @prop */
  colComparisonMode: NBType.enum(ComparisonMode),
  /* @prop */
  countBy: NBType.enum(CountBy),
  /* @prop */
  deStopWords: NBType.boolean(),
  /* @prop */
  dedup: NBType.boolean(),
  /* @prop */
  dma: NBType.boolean(),
  /* @prop */
  docNames: NBType.array().of(NBType.string()),
  /* @prop */
  documentFormat: NBType.enum(DocumentFormat),
  /* @prop */
  emojiCategory: NBType.enum(EmojiCategory),
  /* @prop */
  extraInfo: NBType.enum(ExtraInfo),
  /* @prop */
  facetServiceProperties: FacetServicePropertySchema,
  /* @prop */
  fields: NBType.array().of(NBType.string()),
  /* @prop */
  forceCheckout: NBType.boolean(),
  /* @prop */
  forceNotScale: NBType.boolean(),
  /* @prop */
  groupInfo: NBType.boolean(),
  /* @prop */
  groupMetrics: NBType.array().of(NBType.enum(GroupMetric)),
  /* @prop */
  hashedAuthorIds: NBType.boolean(),
  /* @prop */
  highlightInfo: NBType.boolean(),
  /* @prop */
  ignoreSentiment: NBType.boolean(),
  /* @prop */
  includeHeaderTweet: NBType.boolean(),
  /* @prop */
  keepLongInsights: NBType.boolean(),
  /* @prop */
  localized: NBType.boolean(),
  /* @prop */
  maxExactItem: NBType.number(),
  /* @prop */
  maxPayload: NBType.boolean(),
  /* @prop */
  measure: NBType.enum(Measure),
  /* @prop */
  mediaDomains: NBType.array().of(NBType.enum(MediaDomain)),
  /* @prop */
  mediaTypes: NBType.array().of(NBType.enum(MediaType)),
  /* @prop */
  metricCategory: NBType.enum(MetricCategory),
  /* @prop */
  metricNames: NBType.array().of(NBType.string()),
  /* @prop */
  metricSeries: NBType.array().of(NBType.enum(SupportedMetricSeries)),
  /* @prop */
  multipleLevelSizeNeeded: NBType.array().of(NBType.number()),
  /* @prop */
  offset: NBType.number(),
  /* @prop */
  order: NBType.enum(Order),
  /* @prop */
  pageIndex: NBType.number(),
  /* @prop */
  pageSize: NBType.number(),
  /* @prop */
  payload: PayloadSchema,
  /* @prop */
  peerCount: NBType.number(),
  /* @prop */
  photoConceptServiceProperties: PhotoConceptServicePropertySchema,
  /* @prop */
  postActivityTimeZone: NBType.enum(DayHourTimeZone),
  /* @prop */
  precision: NBType.enum(Precision),
  /* @prop */
  priority: NBType.number(),
  /* @prop */
  removeTweetId: NBType.boolean(),
  /* @prop */
  renewImageUrls: NBType.boolean(),
  /* @prop */
  returnAuthorFingerprint: NBType.boolean(),
  /* @prop */
  rowComparisonMode: NBType.enum(ComparisonMode),
  /* @prop */
  rowDocNames: NBType.array().of(NBType.string()),
  /* @prop */
  rowTagNames: NBType.array().of(NBType.string()),
  /* @prop */
  rowThemeIds: NBType.array().of(NBType.number()),
  /* @prop */
  showPrimaryObject: NBType.boolean(),
  /* @prop */
  sizeNeeded: NBType.number(),
  /* @prop */
  smoothing: NBType.boolean(),
  /* @prop */
  sort: NBType.string(),
  /* @prop */
  sortBy: NBType.enum(SortByOptions),
  /* @prop */
  soundBiteSamples: NBType.number(),
  /* @prop */
  tagNames: NBType.array().of(NBType.string()),
  /* @prop */
  themeIds: NBType.array().of(NBType.number()),
  /* @prop */
  timeUnits: NBType.array().of(NBType.enum(MetricTimeUnit)),
  /* @prop */
  timeZone: NBType.enum(LocalTimeZone),
  /* @prop */
  totalCount: NBType.number(),
  /* @prop */
  turnOffCountSearch: NBType.boolean(),
  /* @prop */
  youTubeOnly: NBType.boolean()
  /* END */
})
export default ServicePropertySchema
export type ServiceProperty = InferType<typeof ServicePropertySchema>
// RESERVE BELOW
